import axios from 'axios';
import moment from 'moment';
import WithBootedClient from '../lib/WithBootedClient';
import { IStore } from '../models/tasks/IStore';
import AuthenticationClient from './AuthenticationClient';
import RESTClient, { IArrayRestResponse } from './RESTClient';
import SettingsClient from './SettingsClient';
import UserClient from './UserClient';
import Secrets from '@yumi/secrets';
import { AppVersion } from '../lib/utils';

interface IConfig {
  baseURL: string;
}

class StoreClient extends RESTClient implements WithBootedClient {
  constructor(config: IConfig) {
    super({baseURL: config.baseURL});
  }

  async boot() {}

  async openStore(): Promise<void> {
    await SettingsClient.set('STORE_IS_OPEN', true);
  }
  async closeStore(): Promise<void> {
    await SettingsClient.set('STORE_IS_OPEN', false);
  }

  async toggleStore(): Promise<void> {
    const storeStatus = (await SettingsClient.get('STORE_IS_OPEN')) as boolean;
    if (storeStatus) {
      await this.closeStore();
      return;
    }
    await this.openStore();
  }

  async getStoreStatus(): Promise<boolean> {
    const storeStatus = await SettingsClient.get('STORE_IS_OPEN');
    return storeStatus !== undefined && storeStatus;
  }
  async setStoreStatus(store: IStore): Promise<void> {
    if (store.updated_at && store.status) {
      const changeStore: Record<'OPENED' | 'CLOSED', () => Promise<void>> = {
        CLOSED: this.closeStore, //IF STORE IS CLOSED, THEN SET AS CLOSED
        OPENED: this.openStore //IF STORE IS OPENED, THEN SET AS OPENED
      };
      /**
       * According to last store update and curret day, check if the store should be opened or closed
       * @param lastUpdate
       * @param currentDay
       * @returns
       */
      const checkCurrentStatus = (
        lastUpdate: string,
        currentDay: string
      ): 'OPENED' | 'CLOSED' => {
        //If store was edited today, then return current status from DB
        if (moment(lastUpdate).format("YYYY-MM-DD") === currentDay) {
          return store.status!;
        }

        //If store wasn't edited today, then return current status according hours (should be already closed from last day at 5 am and opened at 7 pm )

        const result = moment().isBetween(
          moment(`${moment().format('YYYY-MM-DD')} 05:00:00`),
          moment(`${moment().format('YYYY-MM-DD')} 19:00:00`)
        )
          ? 'CLOSED'
          : 'OPENED';
        return result;
      };
      const status = checkCurrentStatus(
        store.updated_at,
        moment().format('YYYY-MM-DD')
      );
      await changeStore[status]();
      return;
    }
    if (store.status === 'OPENED') {
      await this.openStore();
      return;
    }
    await this.closeStore();
  }

  async getStore(): Promise<IStore> {
    const userInfo = AuthenticationClient.getInfo();
    const tasksResponse = await this.getStores(
      'CL',
      userInfo.store
    );
    await this.setStoreStatus(tasksResponse.data[0]);
    return tasksResponse.data[0];
  }

  async getStores(
    country: string,
    id: string
  ): Promise<IArrayRestResponse<IStore>> {
    const response = await this.axios.get(
      `/stores/country/${country}?id=${id}`
    );
    return response.data as IArrayRestResponse<IStore>;
  }

  async getAvailableStoresSM(): Promise<{code: string, name: string}[]> {
    const userInfo = await UserClient.me();
    const stores = await this.getStores('CL', '');
    return stores.data.filter(store => userInfo?.meta_data?.stores.includes(store.id)).map(store => { 
      return {
        code: store.id || '', 
        name: store.description || ''
      }      
    });

  }

  async getStoreById(
    country: string,
    id: string
  ): Promise<IArrayRestResponse<IStore>> {
    const response = await this.axios.get(`/stores/${country}/${id}`);
    return response.data as IArrayRestResponse<IStore>;
  }

  async exist(): Promise<boolean> {
    try {
      return (await this.getStore()) !== undefined;
    } catch (error) {
      return false;
    }
  }

  async getStoresByUserId() {
    try {
      const { data } = await axios.get(`${Secrets.REACT_MDH_USERS_API}/app-bff/stores/mine`, {
        headers: {
          'App-Version': AppVersion,
          Authorization: `Bearer ${AuthenticationClient.state.user?.access_token}`,
          ...this.getCloudflareHeaders(),
        },
      });

      return data;
    } catch (error: any) {
      console.log(error);
      return [];
    }
  }

  private getCloudflareHeaders() {
    return Secrets.REACT_APP_ENVIRONMENT === 'STAGING'
      ? {
          'CF-Access-Client-Id': Secrets.CF_ACCESS_CLIENT_ID,
          'CF-Access-Client-Secret': Secrets.CF_ACCESS_CLIENT_SECRET,
        }
      : {};
  }
  
}

export default new StoreClient({
  baseURL: Secrets.REACT_APP_API_ENDPOINT!,
});
